import { ArrowLeft, Search } from "lucide-react";
import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { wrappedIndex } from "@/lib/wrapped-index";
import { Button } from "./button";
import { Dialog, DialogContent, DialogTitle } from "./dialog";
import { Input } from "./input";
import { MicroLabel } from "./MicroLabel";

export interface CommandItem {
  /** Unique across the whole palette, not only its group: the option in hand is found by it. */
  key: string;
  label: string;
  /** A few words at the right, such as a draft's client. Shown, not searched: `keywords` are what a search reads. */
  hint?: string;
  icon?: ReactNode;
  /** Words it answers to that its label does not carry. */
  keywords?: readonly string[];
  /** Why it cannot be taken here. An item with one is drawn, with the reason as its hint, and never run. */
  disabledReason?: string;
  /** Kept whatever is typed, as the add at the foot of a list is. */
  alwaysShown?: boolean;
  /** Left out until something is typed, so a long list opens on its few most recent. */
  searchOnly?: boolean;
  onSelect: () => void;
}

export interface CommandGroup {
  heading?: string;
  items: readonly CommandItem[];
}

/** A second question the palette is asking, such as which client a new draft is for. */
export interface CommandStep {
  name: string;
  onBack: () => void;
}

/**
 * A search box over everything the attorney can go to or start, opened from
 * anywhere by its shortcut.
 *
 * The box is a combobox over a listbox, as `Switcher`'s is: the focus stays in
 * the box and the arrows move which option is in hand. It lives in the house
 * dialog, which traps the focus while it is open and hands it back on close.
 */
export function CommandPalette({
  open,
  onOpenChange,
  groups,
  step,
  label = "Search clients, drafts and commands",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: readonly CommandGroup[];
  step?: CommandStep;
  label?: string;
}) {
  // Radix hands the focus back to its trigger, and a palette opened by a
  // shortcut has none: without this the focus falls to the page.
  const returnToRef = useRef<HTMLElement | null>(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="top-[12vh] bottom-auto max-w-[600px] gap-0 overflow-hidden p-0"
        onOpenAutoFocus={() => {
          returnToRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnToRef.current?.focus();
        }}
      >
        <DialogTitle className="sr-only">{label}</DialogTitle>
        <PaletteSearch groups={groups} step={step} label={label} />
      </DialogContent>
    </Dialog>
  );
}

function matchesText(item: CommandItem, needle: string): boolean {
  return [item.label, ...(item.keywords ?? [])].some((words) =>
    words.toLowerCase().includes(needle),
  );
}

function matches(item: CommandItem, needle: string): boolean {
  if (item.alwaysShown === true) return true;
  if (needle === "") return item.searchOnly !== true;
  return matchesText(item, needle);
}

function takeable(item: CommandItem): boolean {
  return item.disabledReason === undefined;
}

function resultCount(count: number): string {
  if (count === 0) return "No results";
  return count === 1 ? "1 result" : `${String(count)} results`;
}

/** What a search leaves: the groups still holding something, the options that can be taken, and how many matched. */
function narrow(groups: readonly CommandGroup[], needle: string) {
  const shown = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => matches(item, needle)),
    }))
    .filter((group) => group.items.length > 0);
  const listed = shown.flatMap((group) => group.items);
  return {
    shown,
    // By position rather than key, since a key is the caller's words and an id may not hold a space.
    positions: new Map(listed.map((item, at) => [item, at])),
    inReach: listed.filter(takeable),
    // An always-offered option counts only when the search found it, not when it is there regardless.
    found: listed.filter(
      (item) =>
        item.alwaysShown !== true ||
        (needle !== "" && matchesText(item, needle)),
    ).length,
  };
}

/** The option an arrow moves to from the one in hand, wrapping at either end. */
function keyAfter(
  inReach: readonly CommandItem[],
  inHand: CommandItem | undefined,
  direction: 1 | -1,
): string | null {
  const at = inHand === undefined ? -1 : inReach.indexOf(inHand);
  return inReach.at(wrappedIndex(at, direction, inReach.length))?.key ?? null;
}

/** The box's keys: the arrows move, Enter takes, and Backspace in an empty box goes back a step. */
function paletteKeys({
  inReach,
  inHand,
  query,
  step,
  setActiveKey,
}: {
  inReach: readonly CommandItem[];
  inHand: CommandItem | undefined;
  query: string;
  step: CommandStep | undefined;
  setActiveKey: (key: string | null) => void;
}) {
  return (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setActiveKey(
        keyAfter(inReach, inHand, event.key === "ArrowDown" ? 1 : -1),
      );
    } else if (event.key === "Enter") {
      // The Enter that finishes composing a character belongs to the input method, not the palette.
      if (event.nativeEvent.isComposing) return;
      event.preventDefault();
      inHand?.onSelect();
    } else if (
      // Not a repeat: holding Backspace to clear the box must not also leave the step.
      event.key === "Backspace" &&
      !event.repeat &&
      query === "" &&
      step
    ) {
      event.preventDefault();
      step.onBack();
    }
  };
}

/**
 * What is typed and which option is in hand, started afresh on each question
 * the palette asks. Reset in place rather than by remounting the box, so the
 * focus stays in it from one step to the next.
 */
function useSearch(stepName: string | undefined) {
  const [query, setQuery] = useState("");
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [askedFor, setAskedFor] = useState(stepName);
  if (askedFor !== stepName) {
    setAskedFor(stepName);
    setQuery("");
    setActiveKey(null);
  }
  return { query, setQuery, activeKey, setActiveKey };
}

/** What the status line says: the step on arriving at it, then how many a search found. */
function statusFor(needle: string, found: number, step?: CommandStep): string {
  if (needle !== "") return resultCount(found);
  return step?.name ?? "";
}

/** Keeps the option in hand in view as the arrows move it through a list that scrolls. */
function useInView(optionId: string | undefined) {
  useEffect(() => {
    if (optionId === undefined) return;
    document.getElementById(optionId)?.scrollIntoView({ block: "nearest" });
  }, [optionId]);
}

function PaletteSearch({
  groups,
  step,
  label,
}: {
  groups: readonly CommandGroup[];
  step?: CommandStep;
  label: string;
}) {
  const { query, setQuery, activeKey, setActiveKey } = useSearch(step?.name);
  const id = useId();
  const listId = `${id}-list`;
  const needle = query.trim().toLowerCase();
  const { shown, positions, inReach, found } = narrow(groups, needle);
  // Held by key, so an option that arrives above it while the lists load
  // does not take its place under Enter.
  const inHand =
    inReach.find((item) => item.key === activeKey) ?? inReach.at(0);
  const optionId = (item: CommandItem) =>
    `${id}-option-${String(positions.get(item))}`;
  const onKeyDown = paletteKeys({ inReach, inHand, query, step, setActiveKey });
  const inputRef = useRef<HTMLInputElement>(null);
  useInView(inHand === undefined ? undefined : optionId(inHand));

  return (
    <>
      <PaletteBox step={step} inputRef={inputRef}>
        <Input
          ref={inputRef}
          role="combobox"
          aria-label={step ? `${step.name}: Search` : label}
          aria-expanded
          aria-controls={listId}
          aria-activedescendant={
            inHand === undefined ? undefined : optionId(inHand)
          }
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
          value={query}
          placeholder={
            step ? "Search" : "Search clients and drafts, or type a command"
          }
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveKey(null);
          }}
          onKeyDown={onKeyDown}
          // Lighter than the house field's placeholder, which beside a blinking caret reads as
          // words already typed; 80% of the muted grey still clears 4.5:1 on the card.
          className="h-auto flex-1 border-0 bg-transparent px-0 text-[15px] placeholder:text-muted-foreground/80 focus-visible:ring-0"
        />
      </PaletteBox>
      {needle !== "" && found === 0 && <NoMatch query={query.trim()} />}
      <PaletteList
        id={listId}
        label={label}
        shown={shown}
        inHand={inHand}
        optionId={optionId}
        onHover={(item) => {
          if (takeable(item)) setActiveKey(item.key);
        }}
      />
      <div role="status" className="sr-only">
        {statusFor(needle, found, step)}
      </div>
    </>
  );
}

function NoMatch({ query }: { query: string }) {
  return (
    <p className="px-4 pt-5 pb-2 text-center text-[13px] text-muted-foreground">
      Nothing matches "{query}"
    </p>
  );
}

function PaletteList({
  id,
  label,
  shown,
  inHand,
  optionId,
  onHover,
}: {
  id: string;
  label: string;
  shown: readonly CommandGroup[];
  inHand: CommandItem | undefined;
  optionId: (item: CommandItem) => string;
  onHover: (item: CommandItem) => void;
}) {
  return (
    <div
      id={id}
      role="listbox"
      aria-label={label}
      className="max-h-[min(420px,60vh)] overflow-y-auto px-2 pt-1.5 pb-2"
    >
      {shown.map((group, at) => (
        <PaletteGroup
          // Not its position, which shifts when a group before it falls away.
          key={group.heading ?? group.items.at(0)?.key ?? `group-${String(at)}`}
          heading={group.heading}
        >
          {group.items.map((item) => (
            <PaletteOption
              key={item.key}
              id={optionId(item)}
              item={item}
              inHand={item === inHand}
              onHover={() => {
                onHover(item);
              }}
            />
          ))}
        </PaletteGroup>
      ))}
    </div>
  );
}

/**
 * The row the box sits in, behind a search mark, or, on a step, a way back and
 * the step's name. Backspace goes back too, but only for someone who knows it.
 */
function PaletteBox({
  step,
  inputRef,
  children,
}: {
  step?: CommandStep;
  inputRef: RefObject<HTMLInputElement | null>;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 border-b border-border-subtle px-4 py-3">
      {step ? (
        <>
          <Button
            type="button"
            variant="quiet"
            size="dense"
            // Pressing keeps the focus in the box; a click from the keyboard hands it back.
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              step.onBack();
              inputRef.current?.focus();
            }}
            className="-ml-2 gap-1 px-1.5"
          >
            <ArrowLeft aria-hidden className="size-3.5" />
            Back
          </Button>
          <span className="shrink-0 rounded-sm bg-muted px-2 py-0.5 text-[12px] font-semibold text-primary-soft-foreground">
            {step.name}
          </span>
        </>
      ) : (
        <Search
          aria-hidden
          className="size-4 shrink-0 text-subtle-foreground"
        />
      )}
      {children}
    </div>
  );
}

function PaletteGroup({
  heading,
  children,
}: {
  heading?: string;
  children: ReactNode;
}) {
  const headingId = useId();
  if (heading === undefined) return <div role="group">{children}</div>;
  return (
    <div role="group" aria-labelledby={headingId}>
      <MicroLabel as="div" id={headingId} className="block px-2.5 pt-2.5 pb-1">
        {heading}
      </MicroLabel>
      {children}
    </div>
  );
}

function PaletteOption({
  id,
  item,
  inHand,
  onHover,
}: {
  id: string;
  item: CommandItem;
  inHand: boolean;
  onHover: () => void;
}) {
  const disabled = !takeable(item);
  const hint = item.disabledReason ?? item.hint;
  return (
    <div
      id={id}
      role="option"
      aria-selected={inHand}
      aria-disabled={disabled || undefined}
      // Pressing keeps the focus in the box, so the palette does not lose its
      // keyboard under the press.
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onMouseMove={onHover}
      onClick={() => {
        if (!disabled) item.onSelect();
      }}
      className={cn(
        "flex cursor-pointer items-center gap-3 rounded-inset px-2.5 py-2 text-[13.5px]",
        inHand && "bg-muted-hover",
        disabled && "cursor-not-allowed opacity-55",
      )}
    >
      {item.icon !== undefined && (
        <span
          aria-hidden
          className={cn(
            "grid size-7 shrink-0 place-items-center rounded-sm bg-muted text-[11px] font-bold text-primary-soft-foreground [&_svg]:size-[15px]",
            inHand && "bg-primary text-primary-foreground",
          )}
        >
          {item.icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {hint !== undefined && (
        <span className="shrink-0 text-[12px] text-subtle-foreground">
          {hint}
        </span>
      )}
    </div>
  );
}
